import type { AttributionRow, ObservationData } from './artifactDerivations'

export type SourceChunk = { startMs: number; endMs: number }

type SourceChunkInputs = {
  liveRows: readonly AttributionRow[]
  refinedRows: readonly AttributionRow[]
  rawRows: readonly AttributionRow[]
  chorusRows?: readonly AttributionRow[]
  observations: ObservationData
}

type SourceInterval = SourceChunk & { key: string }
type Boundary = { time: number; lane: number; key: string; delta: number }

const RAW_TURN_GAP_MS = 1_500
const OBSERVATION_TAIL_MS = 2_000

const speechIntervals = (rows: readonly AttributionRow[], gapMs = 0): SourceInterval[] => {
  const intervals: SourceInterval[] = []
  const ordered = rows
    .filter(
      (row) =>
        Number.isFinite(row.start_ms) &&
        Number.isFinite(row.end_ms) &&
        row.start_ms >= 0 &&
        row.end_ms > row.start_ms &&
        Boolean(row.text?.trim())
    )
    .sort((a, b) => a.start_ms - b.start_ms)

  for (const row of ordered) {
    const key = row.speaker?.key ?? 'unknown'
    const previous = intervals.at(-1)
    if (previous && previous.key === key && row.start_ms - previous.endMs <= gapMs) {
      previous.endMs = Math.max(previous.endMs, row.end_ms)
    } else {
      intervals.push({ startMs: row.start_ms, endMs: row.end_ms, key })
    }
  }
  return intervals
}

const observationIntervals = (observations: ObservationData): SourceInterval[] => {
  if (!observations.usable) return []
  const snapshots = [...observations.speaking].sort((a, b) => a.t_ms - b.t_ms)
  return snapshots.flatMap((snapshot, index) => {
    const endMs = snapshots[index + 1]?.t_ms ?? snapshot.t_ms + OBSERVATION_TAIL_MS
    if (snapshot.speaking === null || endMs <= snapshot.t_ms) return []
    const participants = Array.from(new Set(snapshot.speaking)).sort()
    return [{ startMs: snapshot.t_ms, endMs, key: JSON.stringify(participants) }]
  })
}

export const deriveSourceChunks = ({
  liveRows,
  refinedRows,
  rawRows,
  chorusRows = [],
  observations,
}: SourceChunkInputs): SourceChunk[] => {
  const raw = [...speechIntervals(rawRows, RAW_TURN_GAP_MS), ...speechIntervals(chorusRows)]
  const sources = [speechIntervals(liveRows), observationIntervals(observations), raw, speechIntervals(refinedRows)]
  const boundaries: Boundary[] = sources
    .flatMap((intervals, lane) =>
      intervals.flatMap(({ startMs, endMs, key }) => [
        { time: startMs, lane, key, delta: 1 },
        { time: endMs, lane, key, delta: -1 },
      ])
    )
    .sort((a, b) => a.time - b.time)
  const active = sources.map(() => new Map<string, number>())
  const chunks: SourceChunk[] = []
  let previousSignature = ''

  for (let index = 0; index < boundaries.length;) {
    const startMs = boundaries[index].time
    while (index < boundaries.length && boundaries[index].time === startMs) {
      const { lane, key, delta } = boundaries[index]
      const count = (active[lane].get(key) ?? 0) + delta
      if (count === 0) active[lane].delete(key)
      else active[lane].set(key, count)
      index += 1
    }
    const endMs = boundaries[index]?.time
    if (endMs === undefined || endMs <= startMs) continue
    // Raw timings avoid treating coarse transcript rows spanning long pauses as continuous speech.
    const hasSpeech = raw.length > 0 ? active[2].size > 0 : active[0].size > 0 || active[3].size > 0
    if (!hasSpeech) continue
    const signature = JSON.stringify(active.map((identities) => Array.from(identities.keys()).sort()))
    const previous = chunks.at(-1)
    if (previous && previous.endMs === startMs && previousSignature === signature) previous.endMs = endMs
    else chunks.push({ startMs, endMs })
    previousSignature = signature
  }
  return chunks
}

// In a gap, retain the preceding chunk so Right advances to the next speech.
export const sourceChunkIndexAtTime = (chunks: readonly SourceChunk[], timeMs: number): number => {
  if (!Number.isFinite(timeMs)) return -1
  let low = 0
  let high = chunks.length
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (chunks[middle].startMs <= timeMs) low = middle + 1
    else high = middle
  }
  return low - 1
}

export const adjacentSourceChunkIndex = (chunks: readonly SourceChunk[], timeMs: number, direction: -1 | 1): number => {
  const index = sourceChunkIndexAtTime(chunks, timeMs) + direction
  return index >= 0 && index < chunks.length ? index : -1
}
